import { memo } from 'react'
import { cn } from '@/utils/cn'
import { useTranslation } from '@/i18n'

interface StreakDisplayProps {
  currentStreak: number
  longestStreak: number
  lastCheckIn?: Date | null
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

const StreakDisplay = memo(function StreakDisplay({ 
  currentStreak, 
  longestStreak, 
  lastCheckIn: _lastCheckIn,
  className,
  size = 'md'
}: StreakDisplayProps) {
  const { t } = useTranslation()
  
  const sizeClasses = {
    sm: {
      container: 'gap-2',
      icon: 'w-4 h-4',
      number: 'text-lg',
      label: 'text-xs'
    },
    md: {
      container: 'gap-3',
      icon: 'w-5 h-5',
      number: 'text-2xl',
      label: 'text-sm'
    },
    lg: {
      container: 'gap-4',
      icon: 'w-6 h-6',
      number: 'text-3xl',
      label: 'text-base'
    }
  }
  
  const classes = sizeClasses[size]
  const isStreakActive = currentStreak > 0
  
  return (
    <div className={cn('flex items-center', classes.container, className)}>
      <div className="text-center">
        <div className="flex items-center gap-1">
          <svg 
            className={cn(
              classes.icon,
              isStreakActive ? 'text-orange-500' : 'text-gray-400'
            )} 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" />
          </svg>
          <span className={cn(
            'font-bold',
            classes.number,
            isStreakActive ? 'text-orange-500' : 'text-gray-400'
          )}>
            {currentStreak}
          </span>
        </div>
        <p className={cn('text-text-muted', classes.label)}>
          {t('challenges.currentStreak')}
        </p>
      </div>
      
      <div className="text-center">
        <div className="flex items-center gap-1">
          <svg 
            className={cn(classes.icon, 'text-gray-600')} 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className={cn('font-bold', classes.number, 'text-gray-600')}>
            {longestStreak}
          </span>
        </div>
        <p className={cn('text-text-muted', classes.label)}>
          {t('challenges.longestStreak')}
        </p>
      </div>
    </div>
  )
})

const StreakBadge = memo(function StreakBadge({ streak, size = 'md' }: { streak: number; size?: 'sm' | 'md' | 'lg' }) {
  if (streak === 0) return null
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  }
  
  const getMilestone = (streak: number) => {
    if (streak >= 365) return { emoji: '🏆', color: 'bg-yellow-500' }
    if (streak >= 100) return { emoji: '💎', color: 'bg-gray-600' }
    if (streak >= 30) return { emoji: '🔥', color: 'bg-orange-500' }
    if (streak >= 7) return { emoji: '⭐', color: 'bg-blue-500' }
    if (streak >= 3) return { emoji: '✨', color: 'bg-green-500' }
    return { emoji: '🔥', color: 'bg-gray-500' }
  }
  
  const { emoji, color } = getMilestone(streak)
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full text-white font-medium',
      color,
      sizeClasses[size]
    )}>
      <span>{emoji}</span>
      <span>{streak}</span>
    </span>
  )
})

export { StreakDisplay, StreakBadge }